import { useState, useCallback, useEffect, RefObject } from 'react';

interface Coords {
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    isFlipped: boolean;
}

interface UseFloatingPositionOptions {
    maxHeight?: number;
    offset?: number;
}

/**
 * useFloatingPosition - Shared hook for calculating coordinates of a portal-based floating element (Select, Dropdown, etc.)
 */
export function useFloatingPosition(
    triggerRef: RefObject<HTMLElement>,
    dropdownRef: RefObject<HTMLElement>,
    isOpen: boolean,
    options: UseFloatingPositionOptions = {}
) {
    const { maxHeight = 400, offset = 4 } = options;
    const [coords, setCoords] = useState<Coords | null>(null);

    const updateCoords = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        
        // Use dropdown's actual height if it's already rendered, otherwise fallback to maxHeight for conservative flipping
        const actualHeight = dropdownRef.current?.offsetHeight || maxHeight;
        
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        const shouldFlip = spaceBelow < actualHeight && spaceAbove > spaceBelow;

        setCoords({
            top: shouldFlip ? undefined : rect.bottom,
            bottom: shouldFlip ? window.innerHeight - rect.top : undefined,
            left: rect.left,
            width: rect.width,
            isFlipped: shouldFlip
        });
    }, [triggerRef, dropdownRef, isOpen, maxHeight]);

    useEffect(() => {
        if (isOpen) {
            updateCoords();
            window.addEventListener('resize', updateCoords);
            window.addEventListener('scroll', updateCoords, true);

            // A small delay to ensure the dropdown has rendered and has a height
            const timer = setTimeout(updateCoords, 60);

            return () => {
                window.removeEventListener('resize', updateCoords);
                window.removeEventListener('scroll', updateCoords, true);
                clearTimeout(timer);
            };
        } else {
            setCoords(null);
        }
    }, [isOpen, updateCoords]);

    return { coords, updateCoords, offset };
}
